import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { theme } from '../utils/theme';

// Sample gallery items - you can modify this array to add more items
const galleryItems = [
  {
    id: 1,
    image: '/gallery/gallery_image_4.jpeg',
    video: '/gallery/gallery_video_4.mp4',
  },
  {
    id: 2,
    image: '/gallery/gallery_image_2.jpeg',
    video: '/gallery/gallery_video_2.mp4',
  },
  {
    id: 3,
    image: '/gallery/gallery_image_5.jpeg',
    video: '/gallery/gallery_video_5.mp4',
  },
  {
    id: 4,
    image: '/gallery/gallery_image_6.jpeg',
    video: '/gallery/gallery_video_6.mp4',
  },
  {
    id: 5,
    image: '/gallery/gallery_image_1.jpeg',
    video: '/gallery/gallery_video_1.mp4',
  }
  // Add more items as needed
];

const GalleryCard = ({ image, video }) => {
  const videoRef = useRef(null);
  const hasAttemptedPlay = useRef(false);

  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    
    const attemptPlay = () => {
      if (!hasAttemptedPlay.current) {
        hasAttemptedPlay.current = true;
        video.play().catch(() => {
          // If first attempt fails, try again with a delay
          setTimeout(() => {
            video.play().catch(console.log);
          }, 100);
        });
      }
    };

    const handleLoadedData = () => attemptPlay();
    const handleCanPlayThrough = () => attemptPlay();

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('canplaythrough', handleCanPlayThrough);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(console.log);
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(video);

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
      video.pause();
      observer.unobserve(video);
    };
  }, []);

  return (
    <div className="relative h-[380px] md:h-[400px] w-[270px] md:w-[300px] mx-2 md:mx-8">
      {/* Video card */}
      <motion.div 
        className="absolute right-0 top-0 w-[270px] md:w-[280px] h-[380px] md:h-[380px] rounded-2xl overflow-hidden bg-white"
      >
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        >
          <source src={video} type="video/mp4" />
        </video>
      </motion.div>

      {/* Image card - simplified positioning */}
      <div 
        className="absolute left-0 bottom-0 w-[90px] md:w-[144px] h-[120px] md:h-[196px] rounded-2xl overflow-hidden bg-white transform -translate-x-[15px] translate-y-[15px]"
      >
        <img
          src={image}
          alt="Gallery item"
          className="w-full h-full object-cover object-center"
        />
      </div>
    </div>
  );
};

const Gallery = () => {
  const { t } = useTranslation('gallery');
  const scrollContainerRef = useRef(null);

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="w-full bg-gradient-to-b bg-white py-20">
      <div className="container mx-auto px-4 mb-16">
        <h2 className={`${theme.text.h2} text-4xl font-bold text-center mb-4`}>
          {t('title')}
        </h2>
        <p className={`${theme.text.body} text-center mb-12 max-w-2xl mx-auto`}>
          {t('description')}
        </p>
      </div>

      <div className="relative max-w-[95%] md:max-w-[90%] mx-auto">
        {/* Left scroll button */}
        <button 
          onClick={() => handleScroll('left')}
          className={`${theme.button.base} ${theme.button.primary} absolute left-0 top-1/2 -translate-y-1/2 z-10 ml-2 md:ml-4 w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center hover:bg-[#0C3B2E] opacity-60 md:opacity-100 hover:opacity-100 transition-opacity`}
        >
          <svg className="w-4 h-4 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Scrollable container */}
        <div 
          ref={scrollContainerRef}
          className="overflow-x-auto hide-scrollbar mx-6"
          style={{
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            overflowY: 'clip'
          }}
        >
          <div className="flex gap-[120px] md:gap-4 px-8 md:px-8 py-4">
            {galleryItems.map((item, index) => (
              <div 
                key={item.id}
                className="flex-none"
                style={{ scrollSnapAlign: 'center' }}
              >
                <GalleryCard
                  image={item.image}
                  video={item.video}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Right scroll button */}
        <button 
          onClick={() => handleScroll('right')}
          className={`${theme.button.base} ${theme.button.primary} absolute right-0 top-1/2 -translate-y-1/2 z-10 mr-2 md:mr-4 w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center hover:bg-[#0C3B2E] opacity-60 md:opacity-100 hover:opacity-100 transition-opacity`}
        >
          <svg className="w-4 h-4 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </section>
  );
};

// Add custom styles to hide scrollbar but keep functionality
const styles = `
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;

// Add styles to head
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default Gallery;